import React from 'react';
import clsx from 'clsx';
import {Spacing} from '@treatwell/design-tokens';

import {Base} from '../Base';
import styles from './Badge.module.css';
import {ColorPair} from './colour-pair';

export type BadgeSize = Extract<Spacing, 'sm' | 'lg'>;

export interface Props {
  children?: React.ReactNode;
  /**
   * A pair of background and foreground colours.
   * For example `ColorPairTeal` will use `ColorTeal100` and `ColorTeal10`.
   */
  colorPair: ColorPair;
  size?: BadgeSize;
  className?: string;
}

/**
 * The badge component is a base that can be composed to create circular badge components.
 * Its children will be centred within its circle.
 */
export const Badge = ({
  children,
  colorPair,
  size = 'sm',
  className,
  ...props
}: Props) => {
  const spaceMap = {
    sm: styles._2md,
    lg: styles.lg,
  } as const;
  const badgeStyles = clsx(styles.badge, spaceMap[size], className);

  const badgeColorPair = {
    '--Badge-color': colorPair.color,
    '--Badge-backgroundColor': colorPair.backgroundColor,
  } as React.CSSProperties;

  return (
    <Base
      aria-hidden="true"
      className={badgeStyles}
      style={badgeColorPair}
      {...props}
    >
      {children}
    </Base>
  );
};

Badge.displayName = 'Badge';
